/**
  * @class meleAttachments
  * @description
  *
  * # Purpose
  * Implements `<mele-attachments>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-attachments></mele-attachments>'
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-attachments',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleElement from 'meleElement'
import msysListener from 'msysListener'

const PROPERTIES = _.extend({}, {
  files: { type: 'array', description: 'List of attachment files' },
  rendertype: { type: 'string', description: 'Defines the type of elements layout', enum: ['grid', 'list'], default: 'grid' },
}, meleElement._properties)

export default class meleAttachments extends meleElement {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    this.elUploader = this.appendChildren({
      tag: 'mele-uploader',
      readonly: true,
      rendertype: this.rendertype,
    })
    this._listener = new msysListener(0)
    this._listener.publish('#ME', 'mmStart')
    this._listener.mmObjects = this.mmObjects.bind(this)
    super.init()
  } // init

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "files"
  */
  filesChanged(value, old) {
    if(!value || !Array.isArray(value)) return
    this.serverRequest('mmWatch', 0, value)
    this.wait = ' '
  } // filesChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "type"
  */
  rendertypeChanged(value, old) {
    if(this.elUploader) this.elUploader.rendertype = value || PROPERTIES.rendertype?.default;
  } // rendertypeChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * used to return list of workflow processes
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.full] - whether it is full or incremental update
  * @param {boolean} [params.limited] - indicates if data were limited to some fields only
  * @param {integer} [params.version] - version of data sent by server
  * @param {array} [params.sort] - sort criteria
  * @param {object} [params.query] - query parameters
  * @param {string} [params.type] - type of returned objects, e.g. id of workflow producing the objects
  * @param {integer} [params.total] - total number of objects matching the query
  * @param {integer} [params.offset] - offset of returned objects
  * @param {integer} [params.limit] - limits used in the query
  * @param {array} params.list - array of matching objects
  */
  mmObjects(sender, params) {
    this.wait = null
    const ids_received = []
    const items = []

    for(const rec of params.list) {
      ids_received.push(rec.id)

      // Convert base64 data URL to Blob
      const blob = this.dataURLtoBlob(rec.data)
      if (!blob) {
        console.error('Failed to convert data for file:', rec.name)
        continue
      }

      // Create blob URL
      const blobURL = URL.createObjectURL(blob)

      // Prepare file item in the format meleUploader expects
      items.push({
        key: rec.id,
        name: rec.name,
        mime: rec.mime,
        size: rec.size,
        timestamp: rec.updated_dt, // meleFile uses 'timestamp'
        blob: blobURL,
        readonly: true,
      })
    }

    if(params.full) {
      this.elUploader.setItems(items, true)
    } else {
      // Set items directly on the uploader
      this.elUploader.updateItems(items, false)
    }

    this.serverRequest('mmWatch', params.version, ids_received)
  } // mmObjects

  dataURLtoBlob(dataURL) {
    try {
    // Validate input
      if (!dataURL || typeof dataURL !== 'string') {
        console.error('Invalid dataURL: not a string')
        return null
      }

      // Split data URL into parts
      const parts = dataURL.split(',')
      if (parts.length !== 2) {
        console.error('Invalid dataURL format: missing comma separator')
        return null
      }

      // Extract mime type
      const mimeMatch = parts[0].match(/:(.*?);/)
      if (!mimeMatch || !mimeMatch[1]) {
        console.error('Invalid dataURL format: no mime type found')
        return null
      }
      const mime = mimeMatch[1]

      // Decode base64 string
      const base64String = parts[1]
      let binaryString
      try {
        binaryString = atob(base64String)
      } catch (e) {
        console.error('Failed to decode base64 string:', e)
        return null
      }

      // Convert binary string to Uint8Array
      const length = binaryString.length
      const uint8Array = new Uint8Array(length)
      for (let i = 0; i < length; i++) {
        uint8Array[i] = binaryString.charCodeAt(i)
      }

      // Create and return Blob
      return new Blob([uint8Array], { type: mime })
    } catch (error) {
      console.error('Error converting dataURL to Blob:', error)
      return null
    }
  }

  serverRequest(action, version, ids) {
    if(!action || ids.length === 0) return
    const payload = {
      version: version || 0,
      limit: 50,
      sort: ['updated_dt+'],
      fields: ['id', 'data', 'format', 'name', 'mime', 'size', 'updated_dt'],
      query: {
        '~id': '^(' + ids.join('|') + ')$',
      },
    }
    this._listener.publish(':msvrAttachments', action, payload)
  }
} // class meleAttachments

meleAttachments._properties = PROPERTIES
meleAttachments.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-attachments', meleAttachments)
